import type { ReactNode } from 'react';
import { cx, TONE_ICON, TONE_WORD } from '../internal/tone';

export interface CardProps {
  children?: ReactNode;
  title?: string;
  icon?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
  onClick?: () => void;
  className?: string;
}

/**
 * Caixa de conteúdo. Com `onClick` passa a ser um <button> a sério — assim
 * funciona com teclado e é anunciado como clicável, o que uma <div onClick>
 * nunca é.
 *
 * Com tom != neutral ganha barra lateral colorida + ícone do tom + a palavra do
 * tom (esta só para leitor de tela, porque o conteúdo visível do cartão já
 * costuma dizer do que se trata).
 */
export function Card({
  children,
  title,
  icon,
  tone = 'neutral',
  onClick,
  className,
}: CardProps) {
  const shownIcon = icon ?? (tone === 'neutral' ? undefined : TONE_ICON[tone]);

  const inner = (
    <>
      {tone !== 'neutral' ? <span className="flos-sr-only">{TONE_WORD[tone]}. </span> : null}
      {title || shownIcon ? (
        <div className="flos-card__head">
          {shownIcon ? (
            <span className="flos-card__icon flos-icon" aria-hidden="true">
              {shownIcon}
            </span>
          ) : null}
          {title ? <h3 className="flos-card__title">{title}</h3> : null}
        </div>
      ) : null}
      {children}
    </>
  );

  const classes = cx(
    'flos-card',
    tone !== 'neutral' && `flos-card--${tone}`,
    onClick && 'flos-card--clickable',
    className,
  );

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick}>
        {inner}
      </button>
    );
  }

  return <div className={classes}>{inner}</div>;
}
